import React, { useState } from 'react';
import {
  Drawer,
  Avatar,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import InputField from '../common/fields/InputField';
import PrimaryButton from '../common/buttons/PrimaryButton';
import AppSnackbar from '../common/popups/AppSnackbar';
import ConfirmDialog from '../common/popups/ConfirmDialog';
import {
  adminCreateReviews,
  adminUpdateReview,
  adminDeleteReview,
  adminCreateRecommendations,
  adminUpdateRecommendation,
  adminDeleteRecommendation,
} from '../../services/admin.service';

const TIMEFRAME_OPTIONS = [
  { id: 1, label: '7 Days Review' },
  { id: 2, label: '14 Days Review' },
  { id: 3, label: '1 Month Review' },
  { id: 4, label: '2 Months Review' },
  { id: 5, label: '3 Months Review' },
];

const selectStyles = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: '#F6911D' },
    '&.Mui-focused fieldset': { borderColor: '#F6911D' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#F6911D' },
  '& .MuiSelect-select': { color: 'white' },
};

const menuProps = {
  PaperProps: {
    sx: {
      backgroundColor: '#1a1a1a',
      color: 'white',
      '& .MuiMenuItem-root': {
        '&:hover': { backgroundColor: 'rgba(246, 145, 29, 0.1)' },
        '&.Mui-selected': {
          backgroundColor: 'rgba(246, 145, 29, 0.2)',
          '&:hover': { backgroundColor: 'rgba(246, 145, 29, 0.3)' },
        },
      },
    },
  },
};

const emptyReviewForm = {
  reviewer_name: '',
  rating: 5,
  comment: '',
  timeframe: 1,
};

const emptyRecForm = {
  recommender_name: '',
  recommender_relation: '',
  body: '',
};

export default function ReviewsRecsDrawer({ open, onClose, teacher, onUpdate }) {
  const [tab, setTab] = useState('reviews');

  // Forms
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showRecForm, setShowRecForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editingRec, setEditingRec] = useState(null);
  const [reviewForm, setReviewForm] = useState(emptyReviewForm);
  const [recForm, setRecForm] = useState(emptyRecForm);

  // Delete
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  // Snackbar
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  if (!teacher) return null;

  const fullName = `${teacher.firstName} ${teacher.lastName}`;

  // Delete
  const handleDeleteClick = (item, type) => {
    setDeleteTarget(item);
    setDeleteType(type);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteType === 'review') {
        await adminDeleteReview(deleteTarget.id);
      } else {
        await adminDeleteRecommendation(deleteTarget.id);
      }
      setSnackbarMessage(`${deleteType} deleted successfully!`);
      setSnackbarSeverity('success');
      setShowSnackbar(true);
      onUpdate();
    } catch (err) {
      console.error(err);
      setSnackbarMessage(`Failed to delete ${deleteType}!`);
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    } finally {
      setConfirmOpen(false);
      setDeleteTarget(null);
      setDeleteType('');
    }
  };

  // Reviews
  const handleAddReview = () => {
    setEditingReview(null);
    setReviewForm(emptyReviewForm);
    setShowReviewForm(true);
  };

  const handleEditReview = (review, index) => {
    setEditingReview(index);
    setReviewForm({ ...review });
    setShowReviewForm(true);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReview !== null) {
        await adminUpdateReview(teacher.reviews[editingReview].id, reviewForm);
        setSnackbarMessage('Review updated successfully!');
      } else {
        await adminCreateReviews({
          teacher_profile_id: teacher.profileId,
          ...reviewForm,
        });
        setSnackbarMessage('Review added successfully!');
      }
      setSnackbarSeverity('success');
      setShowSnackbar(true);
      onUpdate();
      setShowReviewForm(false);
      setEditingReview(null);
      setReviewForm(emptyReviewForm);
    } catch (err) {
      console.error(err);
      setSnackbarMessage('Failed to save review!');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    }
  };

  // Recommendations
  const handleAddRec = () => {
    setEditingRec(null);
    setRecForm(emptyRecForm);
    setShowRecForm(true);
  };

  const handleEditRec = (rec, index) => {
    setEditingRec(index);
    setRecForm({ ...rec });
    setShowRecForm(true);
  };

  const handleRecSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRec !== null) {
        await adminUpdateRecommendation(teacher.recommendations[editingRec].id, recForm);
        setSnackbarMessage('Recommendation updated successfully!');
      } else {
        await adminCreateRecommendations({
          teacher_profile_id: teacher.profileId,
          ...recForm,
        });
        setSnackbarMessage('Recommendation added successfully!');
      }
      setSnackbarSeverity('success');
      setShowSnackbar(true);
      onUpdate();
      setShowRecForm(false);
      setEditingRec(null);
      setRecForm(emptyRecForm);
    } catch (err) {
      console.error(err);
      setSnackbarMessage('Failed to save recommendation!');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 480 },
          background: 'linear-gradient(135deg, #111111 0%, #1a1a1a 100%)',
          borderLeft: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <Avatar
            src={teacher.avatar ?? undefined}
            sx={{
              width: 40,
              height: 40,
              background: 'linear-gradient(90deg, #E54004 0%, #F6911D 100%)',
              fontWeight: 'bold',
            }}
          >
            {teacher.firstName?.charAt(0)}
          </Avatar>
          <div>
            <p className="text-white font-semibold">{fullName}</p>
            <p className="text-gray-400 text-xs">Reviews & Recommendations</p>
          </div>
        </div>
        <IconButton
          onClick={onClose}
          sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10 shrink-0">
        {['reviews', 'recommendations'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 py-3 text-sm font-semibold capitalize transition-all duration-200 ${
              tab === t
                ? 'text-orange-400 border-b-2 border-orange-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {t}
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-white/10">
              {t === 'reviews'
                ? (teacher.reviews?.length ?? 0)
                : (teacher.recommendations?.length ?? 0)}
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {/* Reviews */}
        {tab === 'reviews' && (
          <>
            <button
              onClick={handleAddReview}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-orange-500/40 text-orange-400 hover:bg-orange-500/10 transition text-sm font-medium"
            >
              <AddCircleOutlineIcon fontSize="small" />
              Add New Review
            </button>

            {showReviewForm && (
              <form
                onSubmit={handleReviewSubmit}
                className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4"
              >
                <p className="text-white font-semibold text-sm">
                  {editingReview !== null ? 'Edit Review' : 'New Review'}
                </p>

                <InputField
                  label="Reviewer Name"
                  value={reviewForm.reviewer_name}
                  onChange={(e) =>
                    setReviewForm({
                      ...reviewForm,
                      reviewer_name: e.target.value,
                    })
                  }
                  required
                />

                <div>
                  <p className="text-gray-400 text-xs mb-2">Rating *</p>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        type="button"
                        key={star}
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                      >
                        <StarRateRoundedIcon
                          sx={{ fontSize: 28 }}
                          className={
                            star <= reviewForm.rating ? 'text-orange-400' : 'text-gray-600'
                          }
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <FormControl fullWidth sx={selectStyles}>
                  <InputLabel>Timeframe *</InputLabel>
                  <Select
                    value={reviewForm.timeframe}
                    onChange={(e) =>
                      setReviewForm({
                        ...reviewForm,
                        timeframe: Number(e.target.value),
                      })
                    }
                    label="Timeframe *"
                    MenuProps={menuProps}
                    required
                  >
                    {TIMEFRAME_OPTIONS.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <InputField
                  label="Comment"
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                  multiline
                  rows={3}
                  required
                />

                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowReviewForm(false);
                      setEditingReview(null);
                    }}
                    className="px-4 py-2 text-sm text-gray-400 hover:text-white transition"
                  >
                    Cancel
                  </button>
                  <PrimaryButton type="submit">
                    {editingReview !== null ? 'Save Changes' : 'Add Review'}
                  </PrimaryButton>
                </div>
              </form>
            )}

            {teacher.reviews?.length > 0
              ? teacher.reviews.map((review, idx) => (
                  <div
                    key={review.id ?? idx}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-semibold text-sm capitalize">
                          {review.reviewer_name}
                        </p>
                        <div className="flex mt-0.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <StarRateRoundedIcon
                              key={s}
                              sx={{ fontSize: 16 }}
                              className={s <= review.rating ? 'text-orange-400' : 'text-gray-600'}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Chip
                          label={
                            TIMEFRAME_OPTIONS.find((o) => o.id === review.timeframe)?.label ?? ''
                          }
                          size="small"
                          sx={{
                            background: 'rgba(246,145,29,0.15)',
                            color: '#F6911D',
                            border: '1px solid rgba(246,145,29,0.3)',
                            fontSize: '0.65rem',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleEditReview(review, idx)}
                          sx={{
                            color: 'rgba(255,255,255,0.4)',
                            '&:hover': { color: '#F6911D' },
                          }}
                        >
                          <EditNoteIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(review, 'review')}
                          sx={{
                            color: 'rgba(255,255,255,0.4)',
                            '&:hover': { color: '#ef4444' },
                          }}
                        >
                          <DeleteForeverOutlinedIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm">{review.comment}</p>
                  </div>
                ))
              : !showReviewForm && (
                  <p className="text-center text-gray-500 py-10">No reviews yet.</p>
                )}
          </>
        )}

        {/* Recommendations */}
        {tab === 'recommendations' && (
          <>
            <button
              onClick={handleAddRec}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-orange-500/40 text-orange-400 hover:bg-orange-500/10 transition text-sm font-medium"
            >
              <AddCircleOutlineIcon fontSize="small" />
              Add New Recommendation
            </button>

            {showRecForm && (
              <form
                onSubmit={handleRecSubmit}
                className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4"
              >
                <p className="text-white font-semibold text-sm">
                  {editingRec !== null ? 'Edit Recommendation' : 'New Recommendation'}
                </p>

                <InputField
                  label="Recommender Name"
                  value={recForm.recommender_name}
                  onChange={(e) => setRecForm({ ...recForm, recommender_name: e.target.value })}
                  required
                />
                <InputField
                  label="Relation (e.g. Former Student)"
                  value={recForm.recommender_relation}
                  onChange={(e) =>
                    setRecForm({
                      ...recForm,
                      recommender_relation: e.target.value,
                    })
                  }
                  required
                />
                <InputField
                  label="Message"
                  value={recForm.body}
                  onChange={(e) => setRecForm({ ...recForm, body: e.target.value })}
                  multiline
                  rows={4}
                  required
                />

                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRecForm(false);
                      setEditingRec(null);
                    }}
                    className="px-4 py-2 text-sm text-gray-400 hover:text-white transition"
                  >
                    Cancel
                  </button>
                  <PrimaryButton type="submit">
                    {editingRec !== null ? 'Save Changes' : 'Add Recommendation'}
                  </PrimaryButton>
                </div>
              </form>
            )}

            {teacher.recommendations?.length > 0
              ? teacher.recommendations.map((rec, idx) => (
                  <div
                    key={rec.id ?? idx}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-semibold text-sm capitalize">
                          {rec.recommender_name}
                        </p>
                        <p className="text-orange-400 text-xs">{rec.recommender_relation}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <IconButton
                          size="small"
                          onClick={() => handleEditRec(rec, idx)}
                          sx={{
                            color: 'rgba(255,255,255,0.4)',
                            '&:hover': { color: '#F6911D' },
                          }}
                        >
                          <EditNoteIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(rec, 'recommendation')}
                          sx={{
                            color: 'rgba(255,255,255,0.4)',
                            '&:hover': { color: '#ef4444' },
                          }}
                        >
                          <DeleteForeverOutlinedIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm italic">"{rec.body}"</p>
                  </div>
                ))
              : !showRecForm && (
                  <p className="text-center text-gray-500 py-10">No recommendations yet.</p>
                )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Confirmation"
        message={`Are you sure you want to delete this ${deleteType}?`}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {/* Snackbar */}
      <AppSnackbar
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Drawer>
  );
}
